import { clamp } from '@utils/math/maths.js'
import Time from './TimeManager.js'
import { w } from '@utils/state/index.js'

const QUALITY_NAMES = ['VERYLOW', 'LOW', 'MEDIUM', 'HIGH', 'VERYHIGH', 'ULTRA']
const MAX_QUALITY = 5
const SECONDS_THRESHOLD = 3
const MAX_PING_PONG = 2
const LS_KEY = 'webgl-quality'
const RESTART_DELAY = 300

// Ratios of the detected display refresh rate (was a flat 58/52/30 assuming 60Hz).
const HIGH_THRESHOLD_RATIO = 58 / 60
const LOW_THRESHOLD_RATIO = 52 / 60
const CRITICAL_THRESHOLD_RATIO = 30 / 60


function arrayMedian(arr) {
  const sorted = [...arr].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid]
}


export default class QualityManager {

  constructor() {
    this.fps = 0
    this.fpsAverage = 0
    this.quality = MAX_QUALITY

    // Detected display refresh rate, used to scale thresholds
    this.refreshRate = 0

    // GPU render tracking (informational — countFrame() called by WebGLManager)
    this.gpuFps = 0
    this.drawCalls = 0
    this.hasGpuTimer = false

    // CPU frame time in ms — rolling avg (6 frames), set by app.js each frame
    this.cpuTime = 0
    this.cpuValues = new Float32Array(6)
    this.cpuIndex = 0

    // GPU frame time in ms — rolling avg (6 frames), set by WebGLManager via timer queries
    this.gpuTime = 0
    this.gpuValues = new Float32Array(6)
    this.gpuIndex = 0

    // Reactive signals — watch these for live updates
    this.fps$ = w(0)
    this.quality$ = w(MAX_QUALITY)
    this.cpuTime$ = w(0)
    this.gpuTime$ = w(0)
    this.refreshRate$ = w(0)

    this.timer = 0
    this.fpsCount = 0
    this.gpuFpsCount = 0
    this.delay = RESTART_DELAY
    this.bootFrames = 10
    this.paused = false

    this.fpsHistory = new Float64Array(SECONDS_THRESHOLD)
    this.fpsHistoryIndex = 0
    this.qualityHistory = new Float64Array([-1, -1, -1])
    this.pingPongScore = 0
    this.bigPingPongScore = 0
    this.qualityLimit = MAX_QUALITY

    this.needReset = false
    this.needHardReset = false
    this.nextDelay = RESTART_DELAY

    this.onQualityChange = null
    this.onFpsUpdate = null

    const saved = this.readPreset()
    this.pushQuality(saved !== null ? saved : MAX_QUALITY)

    // Self-register — fires before app.js render thanks to earlier registration
    Time.add(this.frame.bind(this))

    QualityManager.instance = this
  }


  // Time callback — uses Time.qualityDt (6-frame moving avg), same as mm56
  frame() {
    if (this.bootFrames > 0) { this.bootFrames--; return }
    if (this.needReset) this.resetMeasures()

    const qualityDt = Time.qualityDt

    if (this.delay > 0) { this.delay -= qualityDt; return }

    this.timer += qualityDt
    this.fpsCount++

    if (this.timer >= 1000) this.updateFps()
  }


  // Called by WebGLManager each time renderer.render() actually fires (≤60fps via gpuFrameAccum).
  countFrame(drawCalls = 0) {
    if (this.bootFrames > 0 || this.delay > 0 || this.paused) return
    this.gpuFpsCount++
    this.drawCalls = drawCalls
  }


  setCpuTime(ms) {
    this.cpuValues[this.cpuIndex] = ms
    this.cpuIndex = (this.cpuIndex + 1) % this.cpuValues.length
    if (this.cpuIndex === 0) {
      let sum = 0
      for (let i = 0; i < this.cpuValues.length; i++) sum += this.cpuValues[i]
      this.cpuTime = sum / this.cpuValues.length
      this.cpuTime$.set(this.cpuTime)
    }
  }


  setGpuTime(ms) {
    this.gpuValues[this.gpuIndex] = ms
    this.gpuIndex = (this.gpuIndex + 1) % this.gpuValues.length
    if (this.gpuIndex === 0) {
      let sum = 0
      for (let i = 0; i < this.gpuValues.length; i++) sum += this.gpuValues[i]
      this.gpuTime = sum / this.gpuValues.length
      this.gpuTime$.set(this.gpuTime)
    }
  }


  forceQuality(q) {
    this.updateQuality(clamp(q, 0, MAX_QUALITY))
    this.scheduleReset(true, RESTART_DELAY)
  }


  reset(delay = RESTART_DELAY) { this.scheduleReset(false, delay) }
  hardReset(delay = RESTART_DELAY) { this.scheduleReset(true, delay) }


  scheduleReset(hard = false, delay = RESTART_DELAY) {
    this.needReset = true
    this.needHardReset = this.needHardReset || hard
    this.nextDelay = delay
  }


  get qualityName() {
    return QUALITY_NAMES[this.quality] ?? 'UNKNOWN'
  }


  // ─── internal ──────────────────────────────────────────────────────────────

  readPreset() {
    try {
      const v = localStorage.getItem(LS_KEY)
      return v !== null && !isNaN(v) ? (v | 0) : null
    } catch {
      return null
    }
  }


  savePreset(v) {
    try { localStorage.setItem(LS_KEY, v) } catch { }
  }


  pushQuality(q) {
    this.qualityHistory[2] = this.qualityHistory[1]
    this.qualityHistory[1] = this.qualityHistory[0]
    this.qualityHistory[0] = q
    this.quality = q
    this.quality$.set(q)

    this.savePreset(q)
    if (this.onQualityChange) this.onQualityChange(q)
  }


  updateFps() {
    if (!this.paused) {
      this.fpsHistory[this.fpsHistoryIndex++] = this.fpsCount
      this.fpsHistoryIndex = this.fpsHistoryIndex % (this.fpsHistory.length + 1)
    }

    this.fps = this.fpsCount
    this.fps$.set(this.fps)
    this.gpuFps = this.gpuFpsCount
    if (this.gpuFpsCount === 0) this.drawCalls = 0
    this.fpsCount = 0
    this.gpuFpsCount = 0
    this.timer = this.timer % 1000

    if (this.onFpsUpdate) this.onFpsUpdate(this.fps, this.fpsAverage, this.quality)

    if (!this.paused && this.fpsHistoryIndex === this.fpsHistory.length) {
      this.updateQuality()
    }
  }


  updateQuality(forced) {
    let newQ = this.qualityHistory[0]
    const avg = arrayMedian(this.fpsHistory)

    const refreshRate = Time.stableFps
    if (Time.hzStable && refreshRate !== this.refreshRate) {
      this.refreshRate = refreshRate
      this.refreshRate$.set(refreshRate)
    }

    if (forced !== undefined) {
      newQ = forced
    } else {
      if (avg <= refreshRate * CRITICAL_THRESHOLD_RATIO) newQ -= 2
      else if (avg < refreshRate * LOW_THRESHOLD_RATIO) newQ -= 1
      else if (avg > refreshRate * HIGH_THRESHOLD_RATIO) newQ += 1
    }

    newQ = clamp(newQ, 0, Math.min(this.qualityLimit, MAX_QUALITY))

    // Ping-pong detection: oscillation between 2 consecutive levels
    if (newQ === this.qualityHistory[0]) {
      this.pingPongScore = Math.max(0, this.pingPongScore - 0.2)
      this.bigPingPongScore = Math.max(0, this.bigPingPongScore - 0.2)
    } else if (newQ !== this.qualityHistory[0] && newQ !== this.qualityHistory[1]) {
      this.pingPongScore = 0
    } else if (newQ === this.qualityHistory[1]) {
      this.pingPongScore++
    }

    // Ping-pong detection: oscillation across 3 levels
    if (newQ === this.qualityHistory[2] && newQ < this.qualityHistory[0])
      this.bigPingPongScore++

    if (this.pingPongScore >= MAX_PING_PONG) {
      this.qualityLimit = Math.min(
        this.qualityHistory[1],
        this.qualityHistory[0],
        this.qualityLimit
      )
      this.pingPongScore = 0
    }

    if (this.bigPingPongScore >= MAX_PING_PONG) {
      this.qualityLimit = Math.min(
        arrayMedian(this.qualityHistory),
        this.qualityLimit
      )
      this.bigPingPongScore = 0
    }

    newQ = Math.min(newQ, this.qualityLimit)
    this.fpsAverage = avg

    if (newQ !== this.quality) this.pushQuality(newQ)
  }


  resetMeasures() {
    this.timer = 0
    this.fpsCount = 0
    this.gpuFpsCount = 0
    if (this.needHardReset) {
      this.qualityLimit = MAX_QUALITY
      this.fpsHistoryIndex = 0
      this.pingPongScore = 0
      this.bigPingPongScore = 0
    }
    this.delay = this.nextDelay || RESTART_DELAY
    this.needReset = this.needHardReset = false
    this.nextDelay = RESTART_DELAY
  }

}
